import {
    Box,
    Button,
    Grid,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    CircularProgress,
    styled,
} from "@mui/material";
import { DatePicker, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "dayjs/locale/fr";
import * as React from "react";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import "./SearchComponent.css";
import { getDisponibilities } from "./redux/searchAction";
import { useTranslation } from "react-i18next";
import TaskAltSharpIcon from "@mui/icons-material/TaskAltSharp";
import { UserStructureResponse } from "../structures/model/StructureModel";
import { RootState } from "../common/redux/store";
import dayjs from "dayjs";
import { useState } from "react";
import { AdministrativeArea, Country } from "../common/redux/countryActions";
import CountrySelect from "../common/components/CountryInput";
import AdmnistrativeAreaSelect from "../common/components/AdmnistrativeAreaSelect";
import { Profession } from "../common/redux/professionAction";
import ProfessionSelect from "../common/components/ProfessionInput";

interface SearchComponentProps {
    onSearchSubmit: (newFilters: Record<string, any>) => void;
}

export const SearchComponent: React.FC<SearchComponentProps> = ({
    onSearchSubmit,
}) => {
    const [alignmentJour, setAlignmentJour] = useState<string | null>(null);
    const [alignmentSemaine, setAlignmentSemaine] = useState<string | null>(
        null
    );
    const [dayOpen, setDayOpen] = useState(false);
    const [startDateValue, setStartDateValue] =
        React.useState<dayjs.Dayjs | null>(null);
    const [endDateValue, setEndDateValue] = useState<dayjs.Dayjs | null>(null);
    const [weekOpen, setWeekOpen] = useState(false);
    const [administrativeArea, setAdministrativeArea] =
        useState<AdministrativeArea | null>(null);
    const [profession, setProfession] = useState<Profession[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(
        null
    );

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const userSelector: UserStructureResponse = useAppSelector(
        (state: RootState) => state.user.data
    );

    const userRoles = useAppSelector(
        (state: RootState) => state.auth.data.roles
    );

    const handleAlignmentJour = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null
    ) => {
        if (newAlignment !== null) {
            setAlignmentJour(newAlignment);
        }
    };

    const handleAlignmentSemaine = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null
    ) => {
        if (newAlignment !== null) {
            setAlignmentSemaine(newAlignment);
        }
    };

    const changeDatePicker = (e: dayjs.Dayjs | null, type: string) => {
        if (e) {
            if (type === "startdate") {
                setStartDateValue(e);
            } else if (type === "enddate") {
                setEndDateValue(e);
            }
        }
    };

    const submitResearch = async (event: any) => {
        event.preventDefault();
        setIsSubmitting(true);

        const filters: Record<string, any> = {};

        const params = {
            page: 1,
            itemsPerPage: 20,
            filters,
            role: userRoles.includes("ROLE_STRUCTURE")
                ? "ROLE_STRUCTURE"
                : "ROLE_PROFESSIONNEL",
        };

        filters["order[createdAt]"] = "asc";

        if (profession) {
            filters["profession"] = profession.map(
                ({ id }) => `/api/professions/${id}`
            );
        }
        if (administrativeArea) {
            filters["administrativeAreas.id"] = administrativeArea.id;
            filters["administrativeArea.id"] = administrativeArea.id;
        }
        if (alignmentJour && alignmentJour !== "indifferent") {
            filters["selection"] = alignmentJour;
        } else if (
            !alignmentJour &&
            alignmentSemaine &&
            alignmentSemaine !== "indifferent"
        ) {
            filters["selection"] = alignmentSemaine;
        }
        if (
            alignmentSemaine &&
            alignmentJour &&
            alignmentSemaine !== "indifferent"
        ) {
            filters["selection"] += `,${alignmentSemaine}`;
        }

        if (startDateValue) {
            filters["dateFin[after]"] = startDateValue.format(
                "YYYY-MM-DDTHH:mm:ss"
            );
        }

        if (endDateValue) {
            filters["dateDemarrage[before]"] = endDateValue.format(
                "YYYY-MM-DDTHH:mm:ss"
            );
        }

        if (selectedCountry) {
            filters["country.id"] = selectedCountry.id;
        }

        onSearchSubmit(filters);
        setIsSubmitting(false);
    };

    return (
        <Stack
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: 0,
                margin: 0,
                width: "100%",
                alignItems: "center",
                color: "#FFFFF0",
                backgroundColor: "#FF8A80",
            }}
        >
            <Grid item sx={{ mb: 3, mt: 6 }}>
                {userSelector.id && userSelector.societe ? (
                    <Typography
                        sx={{
                            gridArea: "title",
                            fontSize: "3ex",
                        }}
                    >
                        {t("language.addPersonnel")}
                    </Typography>
                ) : (
                    <Typography
                        sx={{
                            gridArea: "title",
                            fontSize: "3ex",
                        }}
                    >
                        {t("language.addStructure")}
                    </Typography>
                )}
            </Grid>
            <FormContainer>
                <InputsContainer isStructure={false}>
                    {userSelector.id && userSelector.societe ? (
                        <Grid>
                            <ProfessionSelect
                                value={profession}
                                onChange={setProfession}
                                multiple={true}
                                required
                                sx={{
                                    width: "100%",
                                    backgroundColor: "white",
                                    borderRadius: "5px",
                                }}
                                name="profession"
                            />
                        </Grid>
                    ) : null}
                    <CountrySelect
                        value={selectedCountry}
                        onChange={(newValue: Country) => {
                            setSelectedCountry(newValue);
                            setIsSubmitting(false);
                        }}
                        id="country"
                        name="country"
                        width="320px"
                    />
                    {selectedCountry && (
                        <Stack sx={{ flexDirection: "row", gap: "1rem" }}>
                            <AdmnistrativeAreaSelect
                                country={selectedCountry}
                                value={administrativeArea}
                                onChange={(administrativeArea) => {
                                    setAdministrativeArea(administrativeArea);
                                }}
                                sx={{
                                    width: "320px",
                                    "@media(width >= 1200px)": {
                                        width: "320px",
                                    },
                                    "@media(width >= 1900px)": {
                                        width: "280px",
                                    },
                                }}
                                name="administrativeArea"
                            />
                        </Stack>
                    )}
                </InputsContainer>

                <InputsContainer isStructure={!userSelector.societe}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="fr"
                    >
                        <StyledDatePicker
                            disablePast
                            open={dayOpen}
                            value={startDateValue}
                            onOpen={() => setDayOpen(true)}
                            onClose={() => setDayOpen(false)}
                            onChange={(newValue) => {
                                if (dayjs.isDayjs(newValue)) {
                                    changeDatePicker(newValue, "startdate");
                                }
                            }}
                            slotProps={{
                                textField: {
                                    onClick: () => setDayOpen(true),
                                    value: startDateValue,
                                    label: !startDateValue
                                        ? t("select.date.start")
                                        : null,
                                },
                            }}
                            slots={{
                                day: (startDateValue) => (
                                    <PickersDay
                                        {...startDateValue}
                                        color="primary"
                                    />
                                ),
                            }}
                            name="startDate"
                        />
                    </LocalizationProvider>
                    <LocalizationProvider
                        adapterLocale="fr"
                        dateAdapter={AdapterDayjs}
                    >
                        <StyledDatePicker
                            disablePast
                            open={weekOpen}
                            value={endDateValue}
                            onOpen={() => setWeekOpen(true)}
                            onClose={() => setWeekOpen(false)}
                            onChange={(endDateValue) => {
                                if (dayjs.isDayjs(endDateValue)) {
                                    changeDatePicker(endDateValue, "enddate");
                                }
                            }}
                            slotProps={{
                                textField: {
                                    onClick: () => setWeekOpen(true),
                                    value: endDateValue,
                                    label: !endDateValue
                                        ? t("select.date.end")
                                        : null,
                                },
                            }}
                            slots={{
                                day: (endDateValue) => (
                                    <PickersDay
                                        {...endDateValue}
                                        color="primary"
                                    />
                                ),
                            }}
                            name="endDate"
                        />
                    </LocalizationProvider>
                </InputsContainer>
                <Stack
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "2rem",
                        width: "fit-content",

                        "@media(width >= 768px)": {
                            flexDirection: "row",
                        },
                    }}
                >
                    <ToggleContainer>
                        <ButtonGroup
                            value={alignmentJour}
                            exclusive
                            id="Jour"
                            onChange={handleAlignmentJour}
                            color="warning"
                            aria-label="text alignment"
                        >
                            <MyToggleButton
                                value="jour"
                                aria-label="left aligned"
                                sx={{
                                    borderRadius: "5px",
                                    padding: "0 1.6rem 0 1.8rem",
                                }}
                                data-cy="day-toggle-button"
                            >
                                {t("select.day")}
                            </MyToggleButton>
                            <MyToggleButton
                                value="nuit"
                                aria-label="centered"
                                sx={{
                                    padding: "0 1.65rem 0 2rem",
                                }}
                                data-cy="night-toggle-button"
                            >
                                {t("select.night")}
                            </MyToggleButton>

                            <MyToggleButton
                                value="indifferent"
                                aria-label="right aligned"
                                data-cy="indifferent-toggle-button"
                            >
                                {t("select.indifferent")}
                            </MyToggleButton>
                        </ButtonGroup>
                        <ButtonGroup
                            value={alignmentSemaine}
                            exclusive
                            id="Semaine"
                            onChange={handleAlignmentSemaine}
                            color="warning"
                            aria-label="text alignment"
                        >
                            <MyToggleButton
                                value="semaines"
                                aria-label="left aligned"
                                data-cy="weekdays-toggle-button"
                            >
                                {t("select.period.weekend")}
                            </MyToggleButton>
                            <MyToggleButton
                                value="weekend"
                                aria-label="centered"
                                data-cy="weekend-toggle-button"
                            >
                                Week-End
                            </MyToggleButton>
                            <MyToggleButton
                                value="indifferent"
                                aria-label="right aligned"
                                data-cy="indifferent-toggle-button"
                            >
                                {t("select.indifferent")}
                            </MyToggleButton>
                        </ButtonGroup>
                    </ToggleContainer>
                    <Submit type="submit" onClick={submitResearch}>
                        {isSubmitting ? (
                            <CircularProgress color="inherit" size={24} />
                        ) : (
                            <TaskAltSharpIcon
                                fontSize="small"
                                sx={{ width: 58, height: 58 }}
                            />
                        )}
                    </Submit>
                </Stack>
            </FormContainer>
        </Stack>
    );
};

const MyToggleButton = styled(ToggleButton)({
    background: "white",
    boxShadow:
        "rgba(0, 0, 0, 0.2) 0px 2px 2px, rgba(0, 0, 0, 0.3) 0px 3px 3px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
    borderRadius: "5px",
    fontSize: "13px",
    width: "113px",

    "&.Mui-selected, &.Mui-selected:hover": {
        color: "#36454F",
        background:
            "linear-gradient(to right top, #c1e1c1, #c2e1c3, #c3e1c5, #c5e1c7, #c6e1c9, #c6e1c9, #c6e1c9, #c6e1c9, #c5e1c7, #c3e1c5, #c2e1c3, #c1e1c1)",
    },
    "&:hover": { backgroundColor: "#E5E4E2" },

    "@media (width >= 765px)": {
        width: "95px",
    },

    "@media(width >= 1200px)": {
        width: "120px",
    },
});

const FormContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    paddingBottom: "1rem",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",

    "@media(width >= 1024px)": {
        flexDirection: "row",
    },

    "@media(width >= 1200px)": {
        gap: "3rem",
    },

    "@media(width >= 1899px)": {
        gap: "4rem",
    },
});

const StyledDatePicker = styled(DatePicker)({
    width: "320px",
    backgroundColor: "white",
    borderRadius: "5px",

    "@media(width >= 1200px)": {
        width: "200px",
    },

    "@media(width >= 1900px)": {
        width: "280px",
    },
});

const InputsContainer = styled(Stack)<{ isStructure: boolean }>(
    ({ isStructure }) => ({
        gap: "1rem",

        "@media(width >= 768px)": {
            flexDirection: isStructure ? "row" : "column",
        },
    })
);

const ToggleContainer = styled(Stack)({
    gap: "1rem",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
});

const ButtonGroup = styled(ToggleButtonGroup)({
    height: "55px",
    width: "335px",
    boxShadow:
        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 7px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",

    "@media(width >= 768px)": {
        width: "280px",
        height: "45px",
    },

    "@media(width >= 1200px)": {
        width: "350px",
    },
});

const Submit = styled(Button)({
    borderRadius: "50%",
    width: "50px",
    height: "65px",
    padding: "0.5rem",
    alignSelf: "center",
    color: "white",
    boxShadow:
        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
    background:
        "linear-gradient(to bottom, #fdaaaa, #fda2a2, #fc9999, #fc9191, #fb8888, #f98282, #f87d7d, #f67777, #f47473, #f27070, #f06d6c, #ee6969)",
});
